import { useState } from 'react'
import { motion, AnimatePresence } from 'framer-motion'
import { ChevronRight, ChevronLeft, Sparkles, Heart, Zap, Trophy } from 'lucide-react'
import { Button } from '@/components/ui'
import { CharacterSprite } from '@/components/character'
import { useOnboardingStore } from '@/stores/useOnboardingStore'
import { useUserStore } from '@/stores/useUserStore'
import { useSettingsStore } from '@/stores/useSettingsStore'
import { CHARACTERS, type CharacterId } from '@/types'

const WELCOME_DIALOGUE: Record<CharacterId, string> = {
  hana: "Yay~! I'm so excited to help you be productive! Let's do our best together!",
  yuki: "...So you're my new master. Fine. I'll make sure you don't slack off.",
  momo: "U-um... I'll do my best to support you... Please take care of me...",
  sumire: "Ara ara~ Welcome, dear. Mommy will take such good care of you~",
}

type Step = 'welcome' | 'name' | 'character' | 'tutorial' | 'ready'

export function Onboarding() {
  const { setUsername, completeOnboarding } = useOnboardingStore()
  const { setActiveCharacter } = useUserStore()
  const { contentLevel } = useSettingsStore()

  const [step, setStep] = useState<Step>('welcome')
  const [name, setName] = useState('')
  const [selectedCharacter, setSelectedCharacter] = useState<CharacterId>('hana')

  const handleNext = () => {
    const steps: Step[] = ['welcome', 'name', 'character', 'tutorial', 'ready']
    const currentIndex = steps.indexOf(step)
    if (currentIndex < steps.length - 1) {
      setStep(steps[currentIndex + 1])
    }
  }

  const handleBack = () => {
    const steps: Step[] = ['welcome', 'name', 'character', 'tutorial', 'ready']
    const currentIndex = steps.indexOf(step)
    if (currentIndex > 0) {
      setStep(steps[currentIndex - 1])
    }
  }

  const handleComplete = () => {
    setUsername(name.trim() || 'Adventurer')
    setActiveCharacter(selectedCharacter)
    completeOnboarding()
  }

  const canProceed = () => {
    if (step === 'name') return name.trim().length > 0
    return true
  }

  return (
    <div className="min-h-screen bg-bg-dark flex flex-col">
      {/* Progress dots */}
      <div className="flex justify-center gap-2 pt-8 pb-4">
        {['welcome', 'name', 'character', 'tutorial', 'ready'].map((s) => (
          <motion.div
            key={s}
            className={`w-2 h-2 rounded-full ${
              step === s ? 'bg-primary' : 'bg-bg-card'
            }`}
            animate={{ scale: step === s ? 1.2 : 1 }}
          />
        ))}
      </div>

      {/* Content */}
      <div className="flex-1 flex items-center justify-center px-6">
        <AnimatePresence mode="wait">
          {/* Welcome Step */}
          {step === 'welcome' && (
            <motion.div
              key="welcome"
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              exit={{ opacity: 0, y: -20 }}
              className="text-center max-w-sm"
            >
              <motion.div
                animate={{ y: [0, -10, 0] }}
                transition={{ repeat: Infinity, duration: 2 }}
                className="text-8xl mb-6"
              >
                ✨
              </motion.div>

              <h1 className="font-display font-bold text-4xl text-gradient mb-4">
                WAIFUDO
              </h1>

              <p className="text-text-secondary text-lg mb-2">
                Your waifu-powered productivity app
              </p>

              <p className="text-text-muted text-sm">
                Complete quests, build streaks, and unlock rewards with your anime companions!
              </p>
            </motion.div>
          )}

          {/* Name Step */}
          {step === 'name' && (
            <motion.div
              key="name"
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              exit={{ opacity: 0, y: -20 }}
              className="text-center max-w-sm w-full"
            >
              <motion.div
                animate={{ rotate: [0, -5, 5, 0] }}
                transition={{ repeat: Infinity, duration: 2 }}
                className="text-6xl mb-6"
              >
                👋
              </motion.div>

              <h2 className="font-display font-bold text-2xl mb-2">
                What's your name?
              </h2>

              <p className="text-text-secondary mb-6">
                Your waifu wants to know who she'll be cheering for!
              </p>

              <input
                type="text"
                value={name}
                onChange={(e) => setName(e.target.value)}
                placeholder="Enter your name..."
                className="w-full px-6 py-4 bg-bg-card rounded-2xl border-2 border-transparent focus:border-primary focus:outline-none text-center text-lg font-semibold"
                autoFocus
                maxLength={20}
              />

              <p className="text-xs text-text-muted mt-3">
                You can always change this later
              </p>
            </motion.div>
          )}

          {/* Character Selection Step */}
          {step === 'character' && (
            <motion.div
              key="character"
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              exit={{ opacity: 0, y: -20 }}
              className="text-center max-w-sm w-full"
            >
              <h2 className="font-display font-bold text-2xl mb-2">
                Choose Your Companion
              </h2>

              <p className="text-text-secondary mb-6">
                Who will motivate you on your quest?
              </p>

              <div className="space-y-3 mb-6">
                {(Object.keys(CHARACTERS) as CharacterId[]).map((charId) => {
                  const char = CHARACTERS[charId]
                  const isSelected = selectedCharacter === charId

                  return (
                    <motion.button
                      key={charId}
                      whileTap={{ scale: 0.98 }}
                      onClick={() => setSelectedCharacter(charId)}
                      className={`w-full p-4 rounded-2xl border-2 transition-all flex items-center gap-4 ${
                        isSelected
                          ? 'border-primary bg-primary/10 shadow-glow-pink'
                          : 'border-transparent bg-bg-card hover:border-primary/30'
                      }`}
                    >
                      <div className="w-16 h-16 rounded-xl bg-gradient-to-br from-bg-surface to-bg-dark flex items-center justify-center overflow-hidden">
                        <CharacterSprite
                          characterId={charId}
                          contentLevel={contentLevel}
                          size="sm"
                          showGlow={isSelected}
                          showParticles={false}
                        />
                      </div>

                      <div className="flex-1 text-left">
                        <p className="font-bold text-lg">{char.name}</p>
                        <p className="text-sm text-text-secondary">
                          {char.description}
                        </p>
                      </div>

                      <div
                        className={`w-6 h-6 rounded-full border-2 flex items-center justify-center ${
                          isSelected ? 'border-primary' : 'border-text-muted'
                        }`}
                      >
                        {isSelected && (
                          <motion.div
                            initial={{ scale: 0 }}
                            animate={{ scale: 1 }}
                            className="w-3 h-3 rounded-full bg-primary"
                          />
                        )}
                      </div>
                    </motion.button>
                  )
                })}
              </div>

              {/* Character greeting */}
              <motion.div
                key={selectedCharacter}
                initial={{ opacity: 0 }}
                animate={{ opacity: 1 }}
                className="p-4 bg-bg-card rounded-xl"
              >
                <p className="text-text-secondary italic text-sm">
                  "{WELCOME_DIALOGUE[selectedCharacter]}"
                </p>
              </motion.div>
            </motion.div>
          )}

          {/* Tutorial Step */}
          {step === 'tutorial' && (
            <motion.div
              key="tutorial"
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              exit={{ opacity: 0, y: -20 }}
              className="text-center max-w-sm w-full"
            >
              <h2 className="font-display font-bold text-2xl mb-6">
                How It Works
              </h2>

              <div className="space-y-4 text-left">
                <motion.div
                  initial={{ opacity: 0, x: -20 }}
                  animate={{ opacity: 1, x: 0 }}
                  transition={{ delay: 0.1 }}
                  className="flex items-start gap-4 p-4 bg-bg-card rounded-xl"
                >
                  <div className="w-10 h-10 rounded-xl bg-accent/20 flex items-center justify-center flex-shrink-0">
                    <Zap className="text-accent" size={20} />
                  </div>
                  <div>
                    <p className="font-semibold">Complete Quests</p>
                    <p className="text-sm text-text-secondary">
                      Earn XP by finishing tasks and level up!
                    </p>
                  </div>
                </motion.div>

                <motion.div
                  initial={{ opacity: 0, x: -20 }}
                  animate={{ opacity: 1, x: 0 }}
                  transition={{ delay: 0.2 }}
                  className="flex items-start gap-4 p-4 bg-bg-card rounded-xl"
                >
                  <div className="w-10 h-10 rounded-xl bg-red-400/20 flex items-center justify-center flex-shrink-0">
                    <Heart className="text-red-400" size={20} />
                  </div>
                  <div>
                    <p className="font-semibold">Build Affection</p>
                    <p className="text-sm text-text-secondary">
                      Your waifu grows closer as you stay productive
                    </p>
                  </div>
                </motion.div>

                <motion.div
                  initial={{ opacity: 0, x: -20 }}
                  animate={{ opacity: 1, x: 0 }}
                  transition={{ delay: 0.3 }}
                  className="flex items-start gap-4 p-4 bg-bg-card rounded-xl"
                >
                  <div className="w-10 h-10 rounded-xl bg-gem/20 flex items-center justify-center flex-shrink-0">
                    <Sparkles className="text-gem" size={20} />
                  </div>
                  <div>
                    <p className="font-semibold">Unlock Rewards</p>
                    <p className="text-sm text-text-secondary">
                      Earn gems and pull for exclusive outfits
                    </p>
                  </div>
                </motion.div>

                <motion.div
                  initial={{ opacity: 0, x: -20 }}
                  animate={{ opacity: 1, x: 0 }}
                  transition={{ delay: 0.4 }}
                  className="flex items-start gap-4 p-4 bg-bg-card rounded-xl"
                >
                  <div className="w-10 h-10 rounded-xl bg-primary/20 flex items-center justify-center flex-shrink-0">
                    <Trophy className="text-primary" size={20} />
                  </div>
                  <div>
                    <p className="font-semibold">Track Streaks</p>
                    <p className="text-sm text-text-secondary">
                      Build daily habits and watch your streaks grow
                    </p>
                  </div>
                </motion.div>
              </div>
            </motion.div>
          )}

          {/* Ready Step */}
          {step === 'ready' && (
            <motion.div
              key="ready"
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              exit={{ opacity: 0, y: -20 }}
              className="text-center max-w-sm"
            >
              <motion.div
                animate={{ y: [0, -10, 0] }}
                transition={{ repeat: Infinity, duration: 1.5 }}
                className="w-32 h-40 mx-auto bg-gradient-to-br from-bg-card to-bg-surface rounded-2xl flex items-center justify-center mb-6 overflow-hidden"
              >
                <CharacterSprite
                  characterId={selectedCharacter}
                  contentLevel={contentLevel}
                  size="md"
                  animationState="celebration"
                  showGlow
                  showParticles
                  particleType="confetti"
                />
              </motion.div>

              <h2 className="font-display font-bold text-2xl mb-2">
                You're All Set, {name || 'Adventurer'}!
              </h2>

              <p className="text-text-secondary mb-4">
                {CHARACTERS[selectedCharacter].name} is ready to help you conquer your goals!
              </p>

              <motion.p
                initial={{ opacity: 0 }}
                animate={{ opacity: 1 }}
                transition={{ delay: 0.3 }}
                className="text-primary font-semibold mb-6"
              >
                🎁 You've received 100 gems to start!
              </motion.p>

              <Button onClick={handleComplete} size="lg" fullWidth>
                Start Your Quest! ✨
              </Button>
            </motion.div>
          )}
        </AnimatePresence>
      </div>

      {/* Navigation buttons */}
      <div className="p-6">
        {step !== 'ready' && (
          <div className="flex gap-3">
            {step !== 'welcome' && (
              <Button variant="ghost" onClick={handleBack}>
                <ChevronLeft size={20} />
              </Button>
            )}
            <Button
              onClick={handleNext}
              disabled={!canProceed()}
              fullWidth
            >
              Continue
              <ChevronRight size={20} className="ml-2" />
            </Button>
          </div>
        )}
      </div>
    </div>
  )
}
